import styled from "@emotion/styled";
import {
  Accordion,
  AccordionSection,
  ConvertHTMLtoComponents,
  Spacer,
  Spacings,
  useAccordionControls,
  type AccordionControlsHelpers,
} from "@polestar/component-warehouse-react";
import React, { useEffect, useRef, useState } from "react";

import scrollToRef from "../../helpers/scrollToRef";
import useOnSlugMatch from "../../hooks/useOnSlugMatch";
import { DescriptionList } from "../DescriptionList";

import AccordionSectionWrapper from "./AccordionSectionWrapper";

import {
  type AccordionPanelTable,
  type DocumentAccordionRecord,
} from "~/types";

interface LegalAccordionProps {
  //  These are necessary for structured text
  readonly id: string;
  readonly __typename: string;
  readonly body: string;
  readonly initialExpanded?: boolean;
  readonly panels: DocumentAccordionRecord["panels"];
  readonly article: DocumentAccordionRecord["article"];
  readonly table?: AccordionPanelTable;
  readonly initiallyExpandedAccordionPanels?: {
    parentId: string;
    panels: Record<string, boolean>;
  };
}

interface NestedAccordionProps {
  readonly article: LegalAccordionProps["article"];
  readonly controls: AccordionControlsHelpers;
  readonly initiallyExpandedAccordionPanels: LegalAccordionProps["initiallyExpandedAccordionPanels"];
  readonly panels: DocumentAccordionRecord["panels"];
  readonly parentAccordionId: string;
  readonly onExpandParent: (isExpanded: boolean) => void;
  readonly slug: string;
}
/**
 * Heading 3 and 2 has extra spacings and we still need those spacings for actual headings.
 * This minus margin compensates that when it is an accordion
 */
const StyledDiv = styled.div`
  margin-top: -${Spacings.large};
`;

export const handleUpdateControls = (
  expanded: boolean,
  controls: AccordionControlsHelpers,
  parentAccordionId: string
) => {
  if (expanded && !controls.isExpanded(parentAccordionId)) {
    controls.expand(parentAccordionId);
  }

  if (!expanded && controls.isExpanded(parentAccordionId)) {
    controls.close(parentAccordionId);
  }
};

/**
 * The useExpandOnSlugMatch hook is used to expand the parent accordion(s) when the `article` slug matches.
 */
const useExpandOnSlugMatch = (
  articleSlug: string | undefined,
  controls: AccordionControlsHelpers,
  parentAccordionId: string,
  ref: React.MutableRefObject<null | HTMLDivElement>
) => {
  useOnSlugMatch(articleSlug, () => {
    controls.expand(parentAccordionId);
    setTimeout(() => {
      scrollToRef(ref, 80);
    }, 500);
  });
};

/**
 * The useInitialExpand hook is used to expand the parent accordion when the `initialExpanded` prop is true.
 */
const useInitialExpand = (
  controls: AccordionControlsHelpers,
  parentAccordionId: string,
  initialExpanded: boolean
) => {
  useEffect(() => {
    if (initialExpanded) {
      controls.expand(parentAccordionId);
    }
  }, [controls, initialExpanded, parentAccordionId]);
};

const NestedAccordionSection = ({
  article,
  controls,
  initiallyExpandedAccordionPanels,
  panels,
  parentAccordionId,
  slug,
  onExpandParent,
}: NestedAccordionProps) => {
  return (
    <AccordionSection
      border={false}
      title={article?.title}
      id={parentAccordionId}
      optimizeRendering={false}
      onToggle={(id) => {
        controls.toggle(id);
      }}
    >
      <Accordion
        controls={controls}
        border={false}
        title={article?.title ?? undefined}
      >
        {panels.map((panel) => {
          return (
            <AccordionSectionWrapper
              key={panel.article?.slug}
              initiallyExpandedPanels={initiallyExpandedAccordionPanels?.panels}
              panel={panel}
              controls={controls}
              parentSlug={slug || ""}
              onExpandParent={onExpandParent}
            />
          );
        })}
      </Accordion>
    </AccordionSection>
  );
};

/**
 * The Legal Accordion component is used to display a list of flat or nested accordion panels and
 * it handles deeplinking to a specific panel and expands the parent accordion(s) when the slug matches.
 * Example url: `https://www.polestar.com/uk/legal/privacy/privacy-policy#general-development-of-our-business`
 *
 *
 * *Note:
 * Due to animations during expand/collapse, the scroll is delayed by 500ms to ensure the accordion is fully expanded
 * before attempting to scroll to the ref. This can cause race conditions and any changes therefore need to be tested rigorously.*
 */
const LegalAccordion: React.FC<LegalAccordionProps> = ({
  article,
  body,
  table,
  panels,
  initialExpanded = false,
  initiallyExpandedAccordionPanels,
}: LegalAccordionProps) => {
  const { slug } = article || {};
  const ref = useRef(null);
  const controls = useAccordionControls({ closeOthersOnOpen: true });
  const [expanded, setExpanded] = useState(initialExpanded);

  const parentAccordionId = `accordion-${slug}`;

  const handleExpandParent = (isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  useExpandOnSlugMatch(
    article?.slug ?? undefined,
    controls,
    parentAccordionId,
    ref
  );

  useInitialExpand(controls, parentAccordionId, initialExpanded);

  useEffect(() => {
    handleUpdateControls(expanded, controls, parentAccordionId);
  }, [controls, expanded, parentAccordionId]);

  return (
    <StyledDiv ref={ref}>
      <ConvertHTMLtoComponents data-testid="accordion-body">
        {body}
      </ConvertHTMLtoComponents>
      {table && <DescriptionList table={table} />}
      {(table || body) && (
        <Spacer spacing={{ mobile: Spacings.large, tablet: Spacings.xLarge }} />
      )}
      <Accordion
        controls={controls}
        border={false}
        initialExpanded={{
          ...initiallyExpandedAccordionPanels?.panels,
          [parentAccordionId]: expanded,
        }}
      >
        <NestedAccordionSection
          article={article}
          controls={controls}
          initiallyExpandedAccordionPanels={initiallyExpandedAccordionPanels}
          panels={panels}
          parentAccordionId={parentAccordionId}
          slug={slug ?? ""}
          onExpandParent={handleExpandParent}
        />
      </Accordion>
    </StyledDiv>
  );
};

export default LegalAccordion;
